/** Strip spaces and periods, lowercase — input shape for synonym lookup / identity canonical form. */
function qtoUomNormalizedKey(raw: string): string {
  return raw.replace(/[\s.]+/g, "").toLowerCase();
}

/**
 * Synonyms (keys pre-normalized: no spaces/periods, lowercase) → canonical UOM token.
 * Extend as needed; unknown tokens resolve to the normalized key itself.
 */
const UOM_SYNONYMS: Record<string, string> = {
  sqft: "sf",
  squarefoot: "sf",
  squarefeet: "sf",
  squarefootage: "sf",
  square: "sq",
  sqr: "sq",
  linearfoot: "lf",
  linearfeet: "lf",
  lnft: "lf",
  linft: "lf",
  linealfoot: "lf",
  linealfeet: "lf",
  each: "ea",
  count: "ea",
  cuyd: "cy",
  cubicyard: "cy",
  cubicyards: "cy",
  cubicfoot: "cf",
  cubicfeet: "cf",
  cuft: "cf",
  cubicinch: "ci",
  cubicinches: "ci",
  squareinch: "sqin",
  squareinches: "sqin",
  squareyard: "sy",
  squareyards: "sy",
  sqyard: "sy",
  sqyds: "sy",
  sqyrd: "sy",
  sqyd: "sy",
  foot: "lf",
  feet: "lf",
  ft: "lf",
  inch: "in",
  inches: "in",
  yard: "yd",
  yards: "yd",
  meter: "m",
  metre: "m",
  meters: "m",
  metres: "m",
  centimeter: "cm",
  centimetre: "cm",
  millimeter: "mm",
  millimetre: "mm",
  squaremeter: "m2",
  squaremetre: "m2",
  squaremeters: "m2",
  squaremetres: "m2",
  sqmeter: "m2",
  sqm: "m2",
  squarecentimeter: "cm2",
  squarecentimetre: "cm2",
  squaremillimeter: "mm2",
  squaremillimetre: "mm2",
  cubicmeter: "m3",
  cubicmetre: "m3",
  cubicmeters: "m3",
  cubicmetres: "m3",
  gallon: "gal",
  gallons: "gal",
  gauge: "ga",
  hundredsquarefeet: "sq",
  hundredsquarefoot: "sq",
  pereach: "ea",
};

/**
 * Canonical UOM for QTO review comparisons (lowercase tokens: sf, ft, lf, …).
 * Spaces and periods are removed; matching is case-insensitive on input.
 */
export function canonicalQtoUom(uom: string | null | undefined): string {
  if (uom == null) return "";
  const trimmed = String(uom).trim();
  if (!trimmed) return "";
  const key = qtoUomNormalizedKey(trimmed);
  if (!key) return "";
  return UOM_SYNONYMS[key] ?? key;
}

/** True when both UOMs normalize to the same non-empty canonical token (see `canonicalQtoUom`). */
export function qtoReviewUomsMatch(
  takeoffUom: string | null | undefined,
  productUom: string | null | undefined
): boolean {
  const a = canonicalQtoUom(takeoffUom);
  const b = canonicalQtoUom(productUom);
  return a !== "" && b !== "" && a === b;
}

/** Prefix in line item `notes` before the classification id (see `qtoTagForClassification`). */
export const QTO_TAG = "QTO:";

/** Canonical `notes` token linking a line item to a Togal classification id. */
export function qtoTagForClassification(cls: { id: string }): string {
  return `${QTO_TAG} ${cls.id}`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Parse `QTO: <id>` from line item notes (CSV import / review link). */
export function qtoIdFromLineItemNotes(notes: string | null | undefined): string | null {
  if (notes == null || !String(notes).trim()) return null;
  const t = String(notes).trim();
  const idx = t.indexOf(QTO_TAG);
  if (idx === -1) return null;
  const after = t.slice(idx + QTO_TAG.length).trim();
  const uuid = after.match(UUID_RE);
  if (uuid) return uuid[0];
  const token = after.split(/\s+/)[0];
  return token || null;
}

const CLASSIFICATION_EDIPHI_ID_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Parse ediphi product (UPC) id from a classification field expected to contain a bare UUID.
 * Returns the id when it looks like a UUID, otherwise null.
 */
export function ediphiProductIdFromClassificationEdiphiId(ediphiId: string | null | undefined): string | null {
  if (ediphiId == null) return null;
  const s = String(ediphiId).trim();
  if (!s) return null;
  return CLASSIFICATION_EDIPHI_ID_UUID_RE.test(s) ? s.toLowerCase() : null;
}
